import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Music, ArrowLeft } from 'lucide-react';

type AuthProps = {
  onBack?: () => void;
};

export function Auth({ onBack }: AuthProps = {}) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        if (!username.trim()) {
          throw new Error('Username is required');
        }
        await signUp(email, password, username);
      } else {
        await signIn(email, password);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center p-4">
      <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-2xl shadow-xl p-8 w-full max-w-md relative">
        {onBack && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t('auth.backHome')}</span>
          </button>
        )}

        <div className="flex items-center justify-center mb-8 mt-8">
          <img
            src="/MapSound Logo_White copy copy.png"
            alt="MapSound"
            className="h-20 w-20 object-contain"
          />
        </div>

        <h1 className="text-3xl font-bold text-center mb-2 text-white">{t('auth.title')}</h1>
        <p className="text-gray-400 text-center mb-8">
          {t('auth.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                {t('auth.username')}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border bg-white/10 border-gray-600 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent focus:bg-white/15 transition-all"
                required={isSignUp}
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border bg-white/10 border-gray-600 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent focus:bg-white/15 transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border bg-white/10 border-gray-600 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent focus:bg-white/15 transition-all"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#00c7b7] hover:bg-[#14f0d5] text-gray-900 font-semibold py-3 rounded-lg transition-all hover:shadow-lg hover:shadow-[#00c7b7]/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.loading') : isSignUp ? t('auth.signUp') : t('auth.signIn')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
            }}
            className="text-[#00c7b7] hover:text-[#14f0d5] text-sm font-medium"
          >
            {isSignUp ? t('auth.hasAccount') : t('auth.noAccount')}
          </button>
        </div>
      </div>
    </div>
  );
}
